import { useState, useEffect, useCallback, useRef } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import {
  MessageCircle, Wifi, WifiOff, RefreshCw, LogOut,
  Send, Power, CheckCircle, Clock, Smartphone, Shield, ChevronDown, ChevronUp,
  AlertCircle
} from 'lucide-react';

function pickQrFields(raw) {
  if (!raw) return null;
  if (typeof raw === 'string') return { base64: null, code: raw };
  if (raw.base64 || raw.code) return { base64: raw.base64 || null, code: raw.code || null };
  if (raw.qrcode && typeof raw.qrcode === 'object') {
    return { base64: raw.qrcode.base64 || null, code: raw.qrcode.code || null };
  }
  return null;
}

export default function EvolutionConnectModal({ open, onOpenChange, onConnected }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [actionLoading, setActionLoading] = useState('');
  const [qrData, setQrData] = useState(null);
  const [qrFingerprint, setQrFingerprint] = useState('');
  const [testNumber, setTestNumber] = useState('');
  const [testMessage, setTestMessage] = useState('Test desde CavernicolApp');
  const [qrError, setQrError] = useState('');

  // Proxy section
  const [showProxy, setShowProxy] = useState(false);
  const [proxies, setProxies] = useState([]);
  const [selectedProxy, setSelectedProxy] = useState('');
  const [proxyTesting, setProxyTesting] = useState(false);
  const [proxyAssigning, setProxyAssigning] = useState(false);

  const pollRef = useRef(null);
  const qrRefreshRef = useRef(null);
  const autoConnectRef = useRef(false);

  const fetchInfo = useCallback(async () => {
    try {
      const { data } = await api.get('/tenant/evolution/info');
      setInfo(data);
      try {
        const pRes = await api.get('/tenant/whatsapp-proxies');
        setProxies(pRes.data || []);
      } catch (e) { /* proxies optional */ }
      return data;
    } catch (e) { setInfo({ configured: false, managed_available: true }); return null; }
    finally { setLoading(false); }
  }, []);

  const cleanup = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (qrRefreshRef.current) { clearInterval(qrRefreshRef.current); qrRefreshRef.current = null; }
  }, []);

  useEffect(() => {
    if (open) {
      setLoading(true);
      setQrData(null);
      setQrFingerprint('');
      setQrError('');
      autoConnectRef.current = false;
      fetchInfo();
    } else {
      cleanup();
    }
    return cleanup;
  }, [open, fetchInfo, cleanup]);

  // Auto-connect after info loads
  useEffect(() => {
    if (!loading && info && open && !autoConnectRef.current) {
      const isConnected = info.live_state === 'open';
      if (!isConnected) {
        autoConnectRef.current = true;
        doConnect(false);
      }
    }
  }, [loading, info, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const doConnect = async (forceRefresh) => {
    setConnecting(true);
    setQrData(null);
    setQrError('');
    try {
      const { data } = await api.post('/tenant/evolution/connect',
        forceRefresh ? { force_refresh: true } : {});
      if (data.step === 'already_connected') {
        toast.success('WhatsApp ya conectado');
        cleanup();
        fetchInfo();
        if (onConnected) onConnected();
        return;
      }
      const qr = pickQrFields(data.qrcode);
      if (qr?.base64 || qr?.code) {
        setQrData(qr);
        setQrFingerprint(data.qr_fingerprint || '');
        setQrError('');
        startStatusPolling();
        startQrAutoRefresh();
      } else if (data.step === 'instance_restored' || data.step === 'qr_ready') {
        setQrError('No se pudo generar el QR. La instancia puede haberse perdido — usa "Restablecer sesión".');
        toast.error('QR vacío — intenta restablecer la sesión');
      }
      fetchInfo();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail || 'Error al conectar'));
    } finally { setConnecting(false); }
  };

  const startStatusPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get('/tenant/evolution/status');
        // Check both integration_status and raw state
        const connected = data.integration_status === 'connected' || data.state === 'open';
        if (connected) {
          cleanup();
          setQrData(null);
          toast.success('WhatsApp conectado exitosamente');
          fetchInfo();
          if (onConnected) onConnected();
        }
      } catch (e) { /* silent */ }
    }, 4000);
    // Stop after 3 min
    setTimeout(() => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } }, 180000);
  };

  const startQrAutoRefresh = () => {
    if (qrRefreshRef.current) clearInterval(qrRefreshRef.current);
    qrRefreshRef.current = setInterval(async () => {
      try {
        const { data } = await api.get('/tenant/evolution/qr');
        const qr = pickQrFields(data.qrcode);
        if (qr?.base64 || qr?.code) {
          setQrData(qr);
        }
      } catch (e) { /* silent */ }
    }, 25000); // Refresh every 25s (QR expires ~40-60s)
  };

  const doAction = async (action, endpoint, method = 'post') => {
    setActionLoading(action);
    try {
      const config = { method, url: `/tenant/evolution/${endpoint}` };
      const { data } = await api(config);
      if (endpoint === 'reset-instance' && data?.qrcode) {
        const qr = pickQrFields(data.qrcode);
        if (qr) {
          setQrData(qr);
          setQrFingerprint(data.qr_fingerprint || '');
          startStatusPolling();
          startQrAutoRefresh();
        }
        toast.success('Sesión restablecida — escanea el nuevo QR');
        fetchInfo();
        return;
      }
      toast[data.ok !== false ? 'success' : 'error'](`${action} ${data.ok !== false ? 'exitoso' : 'falló'}`);
      fetchInfo();
      if (endpoint === 'reset-instance' || endpoint === 'logout') {
        doConnect(true);
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setActionLoading(''); }
  };

  const handleSendTest = async () => {
    if (!testNumber.trim()) { toast.error('Número requerido'); return; }
    setActionLoading('Send');
    try {
      const { data } = await api.post('/tenant/evolution/send-test', { number: testNumber, text: testMessage });
      toast[data.ok ? 'success' : 'error'](data.ok ? 'Mensaje enviado' : 'Envío falló');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setActionLoading(''); }
  };

  const handleProxyTest = async () => {
    if (!selectedProxy) return;
    setProxyTesting(true);
    try {
      const { data } = await api.post(`/tenant/whatsapp-proxies/${selectedProxy}/test`);
      const ok = data.success || data.result === 'success';
      toast[ok ? 'success' : 'error'](ok ? 'Proxy funcional' : `Error: ${data.error || 'Fallo'}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setProxyTesting(false); }
  };

  const handleProxyAssign = async () => {
    if (!selectedProxy || !info?.integration_id) return;
    setProxyAssigning(true);
    try {
      await api.post(`/tenant/whatsapp-proxies/${selectedProxy}/assign/${info.integration_id}`);
      toast.success('Proxy asignado');
      fetchInfo();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setProxyAssigning(false); }
  };

  const isConnected = info?.live_state === 'open';
  const isZombieSession = info?.live_state && info.live_state !== 'open' && info.live_state !== 'missing'
    && (info?.status === 'connected' || info?.status === 'pending_qr');
  const isMissingInstance = info?.live_state === 'missing';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="evolution-connect-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#0F172A]">
            <div className="w-8 h-8 rounded-lg bg-emerald-600 flex items-center justify-center">
              <MessageCircle className="w-4 h-4 text-white" />
            </div>
            WhatsApp — Evolution API
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-8 gap-2">
            <div className="w-6 h-6 border-2 border-[#E2E8F0] border-t-[#0F172A] rounded-full animate-spin" />
            <span className="text-xs text-[#94A3B8]">Preparando conexión...</span>
          </div>
        ) : isZombieSession ? (
          <div className="space-y-4" data-testid="evo-zombie-panel">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
              La app muestra &quot;Conectado&quot; pero WhatsApp no está activo ({info.live_state}).
              Los mensajes no llegan ni salen. Genera un QR nuevo o restablece la sesión.
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={() => doConnect(true)} disabled={connecting} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="evo-force-qr-btn">
                <RefreshCw className="w-3 h-3 mr-1" /> Generar QR
              </Button>
              <Button size="sm" variant="outline" onClick={() => {
                if (window.confirm('¿Restablecer sesión Evolution por completo? Tendrás que escanear un QR nuevo.')) {
                  doAction('Restablecer sesión', 'reset-instance');
                }
              }} disabled={!!actionLoading} className="text-red-500" data-testid="evo-zombie-reset-btn">
                <AlertCircle className="w-3 h-3 mr-1" /> Restablecer sesión
              </Button>
            </div>
            {qrData?.base64 && (
              <div className="text-center">
                <img src={qrData.base64} alt="QR WhatsApp" className="w-48 h-48 mx-auto border rounded-lg" data-testid="evo-qr-image" />
              </div>
            )}
          </div>
        ) : isConnected ? (
          /* ── Connected ── */
          <div className="space-y-4" data-testid="evo-connected-panel">
            <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Wifi className="w-4 h-4 text-emerald-500" />
                <span className="text-sm font-medium text-[#0F172A]">{info.instance_name || 'WhatsApp'}</span>
                {info.mode === 'managed' && <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">Managed</span>}
              </div>
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-600" data-testid="evo-status">Conectado</span>
            </div>

            <div className="flex flex-wrap gap-1.5">
              <Button size="sm" variant="outline" onClick={() => doAction('Estado', 'status', 'get')} disabled={!!actionLoading} data-testid="evo-status-btn">
                <CheckCircle className="w-3 h-3 mr-1" /> Estado
              </Button>
              <Button size="sm" variant="outline" onClick={() => doConnect(true)} disabled={connecting || !!actionLoading} data-testid="evo-new-qr-btn">
                <RefreshCw className="w-3 h-3 mr-1" /> Nuevo QR
              </Button>
              <Button size="sm" variant="outline" onClick={() => doAction('Reconectar', 'reconnect')} disabled={!!actionLoading} data-testid="evo-reconnect-btn">
                <RefreshCw className="w-3 h-3 mr-1" /> Reconectar
              </Button>
              <Button size="sm" variant="outline" onClick={() => doAction('Reiniciar', 'restart')} disabled={!!actionLoading} data-testid="evo-restart-btn">
                <Power className="w-3 h-3 mr-1" /> Reiniciar
              </Button>
              <Button size="sm" variant="outline" onClick={() => doAction('Desconectar', 'logout')} disabled={!!actionLoading}
                className="text-red-500 hover:text-red-600" data-testid="evo-logout-btn">
                <LogOut className="w-3 h-3 mr-1" /> Desconectar
              </Button>
              <Button size="sm" variant="outline" onClick={() => {
                  if (window.confirm('Esto borra por completo la sesión actual y crea una nueva desde cero — úsalo solo si los mensajes se envían pero nunca llegan aunque la conexión se vea "Conectada". Tendrás que escanear un QR nuevo. ¿Continuar?')) {
                    doAction('Restablecer sesión', 'reset-instance');
                  }
                }} disabled={!!actionLoading}
                className="text-red-500 hover:text-red-600" data-testid="evo-reset-instance-btn">
                <AlertCircle className="w-3 h-3 mr-1" /> Restablecer sesión
              </Button>
              <Button size="sm" variant="outline" onClick={() => doAction('Registro de webhook', 'webhook/register')} disabled={!!actionLoading}
                data-testid="evo-register-webhook-btn" title="Reintentar el registro del webhook si la conexión funciona pero los mensajes no llegan a CavernicolApp">
                <RefreshCw className="w-3 h-3 mr-1" /> Reintentar webhook
              </Button>
            </div>

            <div className="border-t border-[#F1F5F9] pt-3 space-y-2">
              <p className="text-xs font-semibold text-[#0F172A] flex items-center gap-1"><Send className="w-3 h-3" /> Mensaje de Prueba</p>
              <div className="flex gap-2">
                <Input placeholder="5215512345678" value={testNumber} onChange={e => setTestNumber(e.target.value)} className="text-xs" data-testid="evo-test-number" />
                <Button size="sm" onClick={handleSendTest} disabled={!!actionLoading} className="bg-[#0F172A] text-white shrink-0" data-testid="evo-send-btn">
                  <Send className="w-3 h-3" />
                </Button>
              </div>
            </div>

            {(info.recent_logs || []).length > 0 && (
              <div className="border-t border-[#F1F5F9] pt-3">
                <p className="text-xs font-semibold text-[#0F172A] mb-2 flex items-center gap-1"><Clock className="w-3 h-3" /> Logs</p>
                <div className="space-y-1 max-h-24 overflow-y-auto">
                  {info.recent_logs.slice(0, 5).map((l, i) => (
                    <div key={i} className="flex items-center justify-between text-[10px]">
                      <div className="flex items-center gap-1">
                        {l.status === 'success' ? <CheckCircle className="w-2.5 h-2.5 text-emerald-500" /> : <AlertCircle className="w-2.5 h-2.5 text-red-400" />}
                        <span className="text-[#475569]">{l.action}</span>
                      </div>
                      <span className="text-[#CBD5E1]">{l.created_at ? new Date(l.created_at).toLocaleTimeString() : ''}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          /* ── Disconnected: QR Wizard ── */
          <div className="space-y-4" data-testid="evo-wizard">
            {connecting ? (
              <div className="flex flex-col items-center py-6 gap-2">
                <div className="w-8 h-8 border-2 border-[#E2E8F0] border-t-emerald-600 rounded-full animate-spin" />
                <span className="text-xs text-[#64748B]">Preparando instancia y QR...</span>
              </div>
            ) : qrData?.base64 ? (
              <div className="text-center">
                <div className="bg-white border-2 border-emerald-100 rounded-xl p-4 inline-block">
                  <img src={qrData.base64} alt="QR WhatsApp" className="w-48 h-48 mx-auto" data-testid="evo-qr-image" />
                </div>
                <p className="text-xs text-[#94A3B8] mt-2">WhatsApp → Dispositivos vinculados → Vincular dispositivo</p>
                <div className="flex items-center justify-center gap-2 mt-2">
                  <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                  <span className="text-xs text-[#64748B]">Esperando escaneo... (QR se actualiza automáticamente)</span>
                </div>
                <Button size="sm" variant="outline" onClick={() => doConnect(true)} disabled={connecting} className="mt-3" data-testid="evo-refresh-qr-btn">
                  <RefreshCw className="w-3 h-3 mr-1" /> Actualizar QR
                </Button>
              </div>
            ) : qrData?.code ? (
              <div className="text-center">
                <code className="text-[9px] bg-[#F1F5F9] p-3 rounded block break-all max-h-32 overflow-auto">{qrData.code}</code>
                <p className="text-xs text-[#94A3B8] mt-2">Escanea este código con WhatsApp</p>
                <Button size="sm" variant="outline" onClick={() => doConnect(true)} disabled={connecting} className="mt-3" data-testid="evo-refresh-qr-btn">
                  <RefreshCw className="w-3 h-3 mr-1" /> Actualizar QR
                </Button>
              </div>
            ) : (
              <div className="text-center py-4">
                {isMissingInstance && (
                  <div className="p-3 mb-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800 text-left">
                    La instancia de WhatsApp ya no existe en el servidor (sesión perdida tras reinicio).
                    Se recreará automáticamente al conectar.
                  </div>
                )}
                {qrError && (
                  <div className="p-3 mb-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 text-left">
                    {qrError}
                  </div>
                )}
                <Smartphone className="w-12 h-12 text-emerald-600 mx-auto mb-2" />
                <p className="text-sm text-[#64748B] mb-4">Conecta tu WhatsApp escaneando un código QR.</p>
                <div className="flex flex-wrap justify-center gap-2">
                  <Button onClick={() => doConnect(false)} disabled={connecting} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="evo-connect-btn">
                    {connecting ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <MessageCircle className="w-4 h-4 mr-2" />}
                    Conectar WhatsApp
                  </Button>
                  {(qrError || isMissingInstance) && (
                    <Button size="sm" variant="outline" onClick={() => {
                      if (window.confirm('¿Recrear la instancia Evolution desde cero?')) {
                        doAction('Restablecer sesión', 'reset-instance');
                      }
                    }} disabled={!!actionLoading} className="text-red-500" data-testid="evo-missing-reset-btn">
                      <AlertCircle className="w-3 h-3 mr-1" /> Restablecer sesión
                    </Button>
                  )}
                </div>
              </div>
            )}

            {info?.instance_name && !connecting && (
              <div className="flex flex-wrap gap-1.5 pt-2 border-t border-[#F1F5F9]">
                <Button size="sm" variant="outline" onClick={() => doAction('Estado', 'status', 'get')} disabled={!!actionLoading} data-testid="evo-status-btn">
                  <CheckCircle className="w-3 h-3 mr-1" /> Estado
                </Button>
                <Button size="sm" variant="outline" onClick={() => doAction('Reconectar', 'reconnect')} disabled={!!actionLoading} data-testid="evo-reconnect-btn">
                  <RefreshCw className="w-3 h-3 mr-1" /> Reconectar
                </Button>
                <Button size="sm" variant="outline" onClick={() => doAction('Reiniciar', 'restart')} disabled={!!actionLoading} data-testid="evo-restart-btn">
                  <Power className="w-3 h-3 mr-1" /> Reiniciar
                </Button>
              </div>
            )}
          </div>
        )}

        {/* ── Proxy section ── */}
        <div className="border-t border-[#E2E8F0] pt-3 mt-2">
          <button onClick={() => setShowProxy(!showProxy)}
            className="flex items-center justify-between w-full text-left" data-testid="evo-proxy-toggle">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-[#64748B]" />
              <span className="text-xs font-medium text-[#475569]">Proxy opcional (estabilidad de conexión)</span>
            </div>
            {showProxy ? <ChevronUp className="w-4 h-4 text-[#94A3B8]" /> : <ChevronDown className="w-4 h-4 text-[#94A3B8]" />}
          </button>

          {showProxy && (
            <div className="mt-3 space-y-3" data-testid="evo-proxy-section">
              <p className="text-[11px] text-[#94A3B8]">Opcional. Mejora estabilidad en redes restrictivas.</p>
              <select value={selectedProxy} onChange={e => setSelectedProxy(e.target.value)}
                className="w-full text-xs border border-[#E2E8F0] rounded-lg px-3 py-2 bg-white text-[#0F172A]" data-testid="evo-proxy-select">
                <option value="">Sin proxy</option>
                {proxies.map(p => (
                  <option key={p.id} value={p.id}>{p.name} ({p.host}:{p.port})</option>
                ))}
              </select>
              {selectedProxy && (
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={handleProxyTest} disabled={proxyTesting} data-testid="evo-proxy-test-btn">
                    {proxyTesting ? <RefreshCw className="w-3 h-3 animate-spin mr-1" /> : null} Probar
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleProxyAssign}
                    disabled={proxyAssigning || !info?.integration_id} data-testid="evo-proxy-assign-btn">
                    {proxyAssigning ? <RefreshCw className="w-3 h-3 animate-spin mr-1" /> : null} Aplicar
                  </Button>
                </div>
              )}
              {proxies.length === 0 && (
                <p className="text-[11px] text-[#CBD5E1]">No hay proxies configurados.
                  <a href="/tenant/integrations/proxies" className="text-[#0F172A] underline ml-1">Gestionar proxies</a>
                </p>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
